import _map from 'lodash.map';
import _get from 'lodash.get';
import _set from 'lodash.set';
import _keyBy from 'lodash.keyby';

import { default_settings, default_layout } from '@/defaults';
import { fromSeconds, toSeconds } from './timeFormat';

// eslint-disable-next-line no-useless-escape
const time_part_re = /^\d+[:\d\+\-,]*?(@.*)?$/;
// eslint-disable-next-line no-useless-escape
const time_re = /^(\d[\d:]*)(?:([\-+])(\d[\d:]*))?(@.*)?$/;

function hexColorParser(color: string) {
  if (color.charAt(0) !== '#') {
    return `#${color}`;
  } else {
    return color;
  }
}

function booleanParser(value: string) {
  if ([1, '1', true, 'true', 't'].indexOf(value) > -1) {
    return true;
  } else {
    return false;
  }
}

function timeValueParser(value: string) {
  return toSeconds(value);
}

function timeValidator(value: number, config: any = {}) {
  if (value < 0) {
    return false;
  }

  if (config.duration) {
    return value <= config.duration;
  }

  return false;
}

function viewParser(value: string) {
  if ([1, '1', 'tracks'].indexOf(value) > -1) {
    return 'tracks';
  } else {
    return 'tags';
  }
}

const isEmptyString = (v: any) => v !== '';

// https://stackoverflow.com/a/51567564 + https://stackoverflow.com/a/6444043
export const colorBrightness = (color: string) => {
  let hex = color.replace('#', '');
  if (hex.length == 3) {
    hex = hex.replace(/(.)/g, '$1$1');
  }
  const c_r = parseInt(hex.substr(0, 2), 16);
  const c_g = parseInt(hex.substr(2, 2), 16);
  const c_b = parseInt(hex.substr(4, 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness;
};

const valid_video_options = {
  name: {
    repeatable: false,
    customValueParser: (option: any) => (option || '').trim(),
    customBuilder: (options: any) => encodeURIComponent(options),
    customDefaultValue: (option: any, config: any, data: any) => {
      if (data.name && data.name === option) {
        return option;
      } else {
        return '';
      }
    },
  },
  default_tab: {
    repeatable: false,
    rename: 'settings.default_tab',
    customValueParser: viewParser,
    defaultValue: default_settings.default_tab,
  },
  default_track_name: {
    repeatable: false,
    rename: 'settings.default_track_name',
    customValueParser: (option: any) => (option || '').trim(),
    customValidator: (option: any) => !/^\d+$/.test(option),
    defaultValue: default_settings.default_track_name,
  },
  default_track_color: {
    repeatable: false,
    rename: 'settings.default_track_color',
    customValueParser: hexColorParser,
    customValidator: (option: any) =>
      /^#([0-9a-f]{3}){1,2}$/.test(option) && colorBrightness(option) <= 230,
    customBuilder: (option: any) => (option || '').substr(1),
    defaultValue: default_settings.default_track_color,
  },
  default_track_note: {
    repeatable: false,
    rename: 'settings.default_track_note',
    customValueParser: (option: any) => (option || '').trim(),
    customBuilder: (options: any) => encodeURIComponent(options),
    defaultValue: default_settings.default_track_note,
  },
  default_tag_duration: {
    repeatable: false,
    rename: 'settings.default_tag_duration',
    customValueParser: timeValueParser,
    customValidator: timeValidator,
    customBuilder: (option: any) => fromSeconds(option),
    defaultValue: default_settings.default_tag_duration,
  },
  default_tag_lag_time: {
    repeatable: false,
    rename: 'settings.default_tag_lag_time',
    customValueParser: timeValueParser,
    customValidator: timeValidator,
    customBuilder: (option: any) => fromSeconds(option),
    defaultValue: default_settings.default_tag_lag_time,
  },
  autoplay_next_track: {
    repeatable: false,
    rename: 'settings.autoplay_next_track',
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_settings.autoplay_next_track,
  },
  autoplay_next_tag: {
    repeatable: false,
    rename: 'settings.autoplay_next_tag',
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_settings.autoplay_next_tag,
  },
  continue_last_tag: {
    repeatable: false,
    rename: 'settings.continue_last_tag',
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_settings.continue_last_tag,
  },
  loop: {
    repeatable: false,
    rename: 'settings.loop',
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_settings.loop,
  },
  tagging_mode: {
    repeatable: false,
    rename: 'settings.tagging_mode',
    customValueParser: (option: any) =>
      option === 'manual' ? 'manual' : 'simple',
    defaultValue: default_settings.tagging_mode,
  },
};

const valid_track_options = {
  note: {
    repeatable: false,
    customValueParser: (option: any) => (option || '').trim(),
    customBuilder: (options: any) => encodeURIComponent(options),
    defaultValue: '',
  },
  color: {
    repeatable: false,
    customValueParser: hexColorParser,
    customValidator: (option: any) =>
      /^#([0-9a-f]{3}){1,2}$/.test(option) && colorBrightness(option) <= 230,
    customBuilder: (option: any) => (option || '').substr(1),
    customDefaultValue: (option: any, config: any) =>
      trackColor(config.order - 1),
  },
  tag_duration: {
    repeatable: false,
    customValueParser: timeValueParser,
    customValidator: timeValidator,
    customBuilder: (option: any) => fromSeconds(option),
    defaultValue: default_settings.default_tag_duration,
  },
  tag_lag_time: {
    repeatable: false,
    customValueParser: timeValueParser,
    customValidator: timeValidator,
    customBuilder: (option: any) => fromSeconds(option),
    defaultValue: default_settings.default_tag_lag_time,
  },
};

const valid_tag_options = {
  name: {
    repeatable: false,
    customValueParser: (option: any) => (option || '').trim(),
    customBuilder: (options: any) => encodeURIComponent(options),
    defaultValue: '',
  },
  note: {
    repeatable: false,
    customValueParser: (option: any) => (option || '').trim(),
    customBuilder: (options: any) => encodeURIComponent(options),
    defaultValue: '',
  },
  track: {
    repeatable: false,
    customValueParser: (options: any) => {
      return options
        .split('+')
        .filter(isEmptyString)
        .map((option: any) => {
          return `track_${parseInt(option) - 1}`;
        });
    },
    customBuilder: (options: any, config: any, data: any) => {
      return options
        .map((option: any) => {
          return data.tracks_index[option].order;
        })
        .join('+');
    },
  },
};

const valid_query_options = {
  show_tags: {
    repeatable: false,
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_layout.show_tags,
  },
  show_tracks: {
    repeatable: false,
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_layout.show_tracks,
  },
  show_controls: {
    repeatable: false,
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_layout.show_controls,
  },
  show_empty_tracks: {
    repeatable: false,
    type: 'boolean',
    customValueParser: booleanParser,
    defaultValue: default_layout.show_empty_tracks,
  },
  video_size: {
    repeatable: false,
    customValueParser: (option: any) => (option === '50' ? '50' : '66'),
    defaultValue: default_layout.video_size,
  },
};

export const colors = [
  { id: 'color-preset-2', hex: '#d04043' },
  { id: 'color-preset-3', hex: '#538a6a' },
  { id: 'color-preset-4', hex: '#52575c' },
  { id: 'color-preset-5', hex: '#cb8f21' },
  { id: 'color-preset-6', hex: '#63b465' },
  { id: 'color-preset-7', hex: '#94999e' },
  { id: 'color-preset-8', hex: '#e6c439' },
  { id: 'color-preset-9', hex: '#2c4267' },
  { id: 'color-preset-10', hex: '#6c4b37' },
  { id: 'color-preset-11', hex: '#38668b' },
  { id: 'color-preset-12', hex: '#7b2d2f' },
  { id: 'color-preset-13', hex: '#8c6946' },
  { id: 'color-preset-14', hex: '#775780' },
  { id: 'color-preset-15', hex: '#f4a66c' },
  { id: 'color-preset-16', hex: '#9d446a' },
  { id: 'color-preset-17', hex: '#eb7272' },
];

export const trackColor = (index: number) => {
  return colors[index % colors.length].hex;
};

export const paramHandler = (
  params: any = {},
  valid: any,
  config: any = {}
) => {
  const ret: any = {};

  for (const [param, value] of Object.entries(params)) {
    let param_key = param;
    let param_value = value;

    if (valid.hasOwnProperty(param) && typeof value !== 'undefined') {
      if (Array.isArray(value) && !valid[param].repeatable) {
        param_value = value[value.length - 1];
      }

      if (valid[param].customValueParser) {
        param_value = valid[param].customValueParser(value, config);
      }

      if (valid[param].rename) {
        param_key = valid[param].rename;
      }

      if (valid[param].customValidator) {
        if (!valid[param].customValidator(value, config)) {
          continue;
        }
      }

      _set(ret, param_key, param_value);
    }
  }

  return ret;
};

export const optionParser = (
  data: string[] = [],
  valid: any,
  config: any = {}
) => {
  const ret: any = {};

  for (const data_option of data) {
    const option_parts = data_option.split(':').filter(isEmptyString);
    if (option_parts && option_parts.length > 0) {
      const option = option_parts[0];
      let option_key = option;
      let value: any;

      if (valid.hasOwnProperty(option)) {
        if (option_parts.length > 1) {
          value = option_parts.splice(1).join(':');
        } else {
          if (valid[option].type === 'boolean') {
            value = true;
          }
        }
        if (!value) {
          continue;
        }

        if (valid[option].customValueParser) {
          value = valid[option].customValueParser(value, config);
        }

        if (valid[option].rename) {
          option_key = valid[option].rename;
        }

        if (valid[option].customValidator) {
          if (!valid[option].customValidator(value, config)) {
            continue;
          }
        }

        if (valid[option].repeatable) {
          _set(ret, option_key, [
            ..._get(ret, option_key),
            ...Array.from(value),
          ]);
        } else {
          _set(ret, option_key, value);
        }
      }
    }
  }

  return ret;
};

export const timeParser = (
  data: string = '',
  state: any = {},
  options: any = {},
  config: any = {}
): any => {
  const ret: any = {
    tag_id: state.tag_id || 0,
    tags: [],
  };

  const time_range_parts = data.split(',').filter(isEmptyString);

  time_range_parts.forEach((time) => {
    const time_parts = time.match(time_re);

    if (!time_parts) {
      return;
    }

    let time_item: any = {
      id: `tag_${ret.tag_id++}`,
      track: [],
      start: 0,
      end: 0,
    };

    if (state.track && state.track.id) {
      time_item.track = [state.track.id];
    }

    if (typeof time_parts[1] !== 'undefined') {
      time_item.start = toSeconds(time_parts[1]);
    }

    if (time_parts[3]) {
      if (time_parts[2] === '-') {
        time_item.end = toSeconds(time_parts[3]);
      } else {
        time_item.end = time_item.start + toSeconds(time_parts[3]);
      }

      if (time_item.end <= time_item.start) {
        time_item.end = time_item.start + time_item.end;
      }
    } else {
      if (options.default_tag_duration) {
        time_item.end = time_item.start + options.default_tag_duration;
      }
    }

    if (time_parts[4]) {
      time_item = {
        ...time_item,
        ...optionParser(time_parts[4].split('@'), valid_tag_options, config),
      };
    }

    ret.tags.push(time_item);
  });

  return ret;
};

export const tagParser = (
  data: string,
  query: any = {},
  config: any = {}
): any => {
  let track_id = 0;
  let tag_id = 0;
  let tags = [];

  const ret: any = {
    video: {},
    tracks: [],
    tags: [],
    query: paramHandler(query, valid_query_options, config) || {},
  };

  if (!data) {
    return ret;
  }

  const data_parts = data.split('/').filter(isEmptyString);
  let previous_track: any = null;

  data_parts.forEach((part) => {
    if (time_part_re.test(part)) {
      ({ tag_id, tags } = timeParser(
        part,
        {
          track: previous_track,
          tag_id,
        },
        ret.video.settings || {},
        config
      ));

      ret.tags.push(...tags);
      previous_track = null;
    } else {
      const track_parts = part.split('@').filter(isEmptyString);

      if (part.charAt(0) === '@') {
        ret.video = {
          ...ret.video,
          ...optionParser(track_parts, valid_video_options, config),
        };
      } else {
        let track: any = {
          id: `track_${track_id++}`,
          name: track_parts.shift(),
          color: trackColor(track_id - 1),
          order: track_id,
        };

        track = {
          ...track,
          ...optionParser(track_parts, valid_track_options, config),
        };

        previous_track = track;
        ret.tracks.push(track);
      }
    }
  });

  const track_ids = _map(ret.tracks, 'id');
  ret.tags = ret.tags.map((tag: any) => {
    tag.track = tag.track.filter((track: any) => {
      return track_ids.indexOf(track) > -1;
    });
    return tag;
  });

  return ret;
};

function optionBuilder(
  options: any,
  valid: any,
  data: any = {},
  config: any = {}
) {
  let ret: any = [];

  if (config.return_object) {
    ret = {};
  }

  for (const [key, value] of Object.entries(options || {})) {
    let _value = value;
    if (valid.hasOwnProperty(key) && typeof _value !== 'undefined') {
      if (
        valid[key].hasOwnProperty('defaultValue') &&
        valid[key].defaultValue === _value
      ) {
        continue;
      }

      if (
        valid[key].hasOwnProperty('customDefaultValue') &&
        valid[key].customDefaultValue(_value, options, data) === _value
      ) {
        continue;
      }

      if (valid[key].customBuilder) {
        _value = valid[key].customBuilder(value, options, data);
        if (!_value) {
          continue;
        }
      }

      if (valid[key].hasOwnProperty('type')) {
        if (valid[key].type === 'boolean') {
          if (config.return_object) {
            ret[key] = _value;
          } else {
            ret.push(`@${key}`);
          }
        }
      } else {
        if (config.return_object) {
          ret[key] = _value;
        } else {
          ret.push(`@${key}:${_value}`);
        }
      }
    }
  }

  return ret;
}

export const tagBuilder = (
  video: any = {},
  tracks: any = [],
  tags: any = [],
  query: any = {},
  data: any = {}
): any => {
  const tracks_index = _keyBy(tracks, 'id');
  const path = [];

  // build video meta
  const video_meta = optionBuilder(video, valid_video_options, data);

  // build video settings
  const video_options = optionBuilder(
    video.settings,
    valid_video_options,
    data
  );

  // build tracks
  const part_tracks = [];

  for (const track of tracks.filter((track: any) => track.id !== 'trackless')) {
    const build_track: any = [];

    build_track.push(`${track.name}`);

    const track_options = optionBuilder(track, valid_track_options);
    if (track_options && track_options.length > 0) {
      build_track.push(track_options.join(''));
    }
    part_tracks.push(build_track.join(''));
  }

  // build tags
  const part_tags_trackless = [];
  const part_tags = [];

  for (const tag of tags) {
    const build_tag: any = [];
    let tag_time = '';

    if (typeof tag.start !== 'undefined') {
      tag_time += fromSeconds(tag.start);
    }

    if (tag.end) {
      tag_time += `-${fromSeconds(tag.end)}`;
    }

    build_tag.push(`${tag_time}`);

    const tag_options = optionBuilder(tag, valid_tag_options, {
      tracks_index,
    });

    if (tag_options && tag_options.length > 0) {
      build_tag.push(tag_options.join(''));
    }

    if (tag.track && tag.track.length > 0) {
      part_tags.push(build_tag.join(''));
    } else {
      part_tags_trackless.push(build_tag.join(''));
    }
  }

  // build query
  const query_object = optionBuilder(query, valid_query_options, data, {
    return_object: true,
  });

  const part_query = Object.keys(query_object)
    .map(
      (k) =>
        encodeURIComponent(k) + '=' + encodeURIComponent(query[k] as string)
    )
    .join('&');

  // add video meta
  if (video_meta && video_meta.length > 0) {
    path.push(video_meta.join(''));
  }

  // add video options
  if (video_options && video_options.length > 0) {
    path.push(video_options.join(''));
  }

  // add tag trackless parts
  if (part_tags_trackless && part_tags_trackless.length > 0) {
    path.push(part_tags_trackless.join(','));
  }

  // add track parts
  if (part_tracks && part_tracks.length > 0) {
    path.push(part_tracks.join('/'));
  }

  // add tag parts
  if (part_tags && part_tags.length > 0) {
    path.push(part_tags.join(','));
  }

  const path_string = path.join('/');
  let query_string = '';

  if (part_query) {
    query_string = `?${part_query}`;
  }

  return { path_string, query_string, query_object };
};
